import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import style from './SideBar.module.css';
import { useSelector, useDispatch } from 'react-redux';
import { getUser } from '../../Redux/Actions/actions';
import { 
  FaHome, FaComments, FaChartBar, FaUsers, FaTags, FaRss, FaCalendarAlt, FaTools, FaClipboardList, FaUserPlus, FaCheckCircle, FaMapMarkerAlt, FaBalanceScale, FaRegCalendarPlus, FaRegCalendar,
  FaChevronDown, FaChevronUp, FaEye, FaUserTie, FaHandshake, FaPencilAlt, FaIdCard
} from 'react-icons/fa';
import nuevoLogo from '../../Assets/nuevoLogo.png';

const allowedEmails = [
  'arosa@byraices.com',
  'tsanesteban@byraices.com',
  'dsanesteban@byraices.com',
  'jgarcia@byraices.com'
];

const allowedAdmin = [
  'arosa@byraices.com',
  'dsanesteban@byraices.com',
];

const allowedVRC = [
  'arosa@byraices.com',
  'matencia@byraices.com',
];

const allowedListOnly = [
  'vsosa@byraices.com',
];

const allowedCreateEvent = [
  'arosa@byraices.com',
  'tsanesteban@byraices.com',
  'dsanesteban@byraices.com',
  'jgarcia@byraices.com',
  'mgonella@byraices.com',
  'agutierrez@byraices.com',
  "matencia@byraices.com"
];

const allowedRentals = [
  'arosa@byraices.com',
  'tsanesteban@byraices.com',
  'dsanesteban@byraices.com',
  'jgarcia@byraices.com',
  'mgonella@byraices.com',
  'agutierrez@byraices.com',
  "matencia@byraices.com"
];

// Emails que pueden ver el generador de tarjetas. Editá esta lista para sumar/sacar agentes.
const allowedTarjetas = [
  'arosa@byraices.com',
  'tsanesteban@byraices.com',
  'dsanesteban@byraices.com',
  'jgarcia@byraices.com',
  'lalvarado@byraices.com'
];

const Sidebar = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [vrcOpen, setVrcOpen] = useState(false);
  const sidebarRef = useRef(null);
  let startX = null;
  const dispatch = useDispatch();
  const userId = useSelector(state => state.userId);
  const user = useSelector(state => state.user);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: user,
          userId: user.id
        }
      });
    } else {
      if (userId) {
        dispatch(getUser(userId));
      }
    }
  }, [dispatch, userId]);

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  const showApproveButton = user && user.user && user.user.mail && allowedEmails.includes(user.user.mail);
  const showAllowedAdmin = user && user.user && user.user.mail && allowedAdmin.includes(user.user.mail);
  const showRentals = user && user.user && user.user.mail && allowedRentals.includes(user.user.mail);

  // Para móviles (touch)
  useEffect(() => {
    const handleTouchStart = (e) => {
      startX = e.touches[0].clientX;
    };
    const handleTouchEnd = (e) => {
      if (startX === null) return;
      const endX = e.changedTouches[0].clientX;
      const diff = endX - startX;
      if (diff > 80) setIsExpanded(true); // Swipe derecha: abrir
      if (diff < -80) setIsExpanded(false); // Swipe izquierda: cerrar
      startX = null;
    };
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  // Para escritorio (mouse)
  useEffect(() => {
    let mouseDown = false;
    let mouseStartX = null;
    const handleMouseDown = (e) => {
      mouseDown = true;
      mouseStartX = e.clientX;
    };
    const handleMouseUp = (e) => {
      if (!mouseDown || mouseStartX === null) return;
      const diff = e.clientX - mouseStartX;
      if (diff > 80) setIsExpanded(true); // Arrastrar derecha: abrir
      if (diff < -80) setIsExpanded(false); // Arrastrar izquierda: cerrar
      mouseDown = false;
      mouseStartX = null;
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <div>
      <div className={`${style.sidebar} ${isExpanded ? style.expanded : ''}`} ref={sidebarRef}>
        <div className={style['sidebar-menu']}>
          <Link to="/properties" className={style.link}>
            <FaHome className={style.icon} />
            Propiedades
          </Link>
          <Link to="#" className={style.link}>
            <FaComments className={style.icon} />
            Consultas
          </Link>
          {showRentals && (
            <div className={style.submenuWrapper}>
              <div className={style.link} onClick={() => setStatsOpen(!statsOpen)} style={{ cursor: 'pointer' }}>
                <FaChartBar className={style.icon} />
                <span style={{ flex: 1 }}>Estadísticas</span>
                {statsOpen ? <FaChevronUp style={{ fontSize: 12 }} /> : <FaChevronDown style={{ fontSize: 12 }} />}
              </div>
              {statsOpen && (
                <div className={style.submenu}>
                  <Link to="/estadisticas/visitas" className={style.sublink}>
                    <FaEye className={style.subicon} />
                    Visitas
                  </Link>
                  <Link to="/estadisticas/productividad" className={style.sublink}>
                    <FaUserTie className={style.subicon} />
                    Productividad
                  </Link>
                  <Link to="/estadisticas/reuniones" className={style.sublink}>
                    <FaHandshake className={style.subicon} />
                    Reuniones
                  </Link>
                </div>
              )}
            </div>
          )}
          <Link to="/clientes" className={style.link}>
            <FaUsers className={style.icon} />
            Clientes
          </Link>
          <Link to="/anotar-visita" className={style.link}>
            <FaPencilAlt className={style.icon} />
            Anotar Visita
          </Link>
        
            <div className={style.submenuWrapper}>
              <div className={style.link} onClick={() => setVrcOpen(!vrcOpen)} style={{ cursor: 'pointer' }}>
                <FaTags className={style.icon} />
                VRC
                {vrcOpen ? <FaChevronUp className={style.chevron} /> : <FaChevronDown className={style.chevron} />}
              </div>
              {vrcOpen && (
                <div className={style.submenu}>
                  <Link to="/vrc-pro" className={style.sublink}>+ Nuevo VRC</Link>
                  <Link to="/vrc-lista" className={style.sublink}>Mis VRCs</Link>
                </div>
              )}
            </div>
        
          <Link to="#" className={style.link}>
            <FaRss className={style.icon} />
            Feed
          </Link>
          <Link to="/calendario" className={style.link}>
            <FaCalendarAlt className={style.icon} />
            Calendario
          </Link>
          {allowedCreateEvent.includes(user?.user?.mail) && (
            <Link to="/eventos/crear" className={style.link}>
              <FaRegCalendarPlus className={style.icon} />
              Crear Evento
            </Link>
          )}
          <Link to="/placas" className={style.link}>
            <FaTools className={style.icon} />
            Placas
          </Link>
          {allowedTarjetas.includes(user?.user?.mail) && (
            <Link to="/tarjetas" className={style.link}>
              <FaIdCard className={style.icon} />
              Tarjetas
            </Link>
          )}
          <Link to="#" className={style.link}>
            <FaClipboardList className={style.icon} />
            Reportes
          </Link>
          {showApproveButton && (
            <Link to="/aprobar" className={style.link}>
              <FaCheckCircle className={style.icon} />
              Aprobar
            </Link>
          )}
          {showApproveButton && (
            <Link to="/visitas" className={style.link}>
              <FaMapMarkerAlt className={style.icon} />
              Visitas
            </Link>
          )}
          {showApproveButton && (
            <Link to="/usuarios" className={style.link}>
              <FaUserPlus className={style.icon} />
              Usuarios
            </Link>
          )}
          {showRentals && (
            <Link to="/alquileres" className={style.link}>
              <FaHome className={style.icon} />
              Alquileres
            </Link>
          )}
          {(showApproveButton || (user?.user?.mail && allowedListOnly.includes(user.user.mail))) && (
            <Link to="/lista-administrador" className={style.link}>
              <FaClipboardList className={style.icon} />
              Lista
            </Link>
          )}
          {(showApproveButton || (user?.user?.mail && allowedListOnly.includes(user.user.mail))) && (
            <Link to="/dashboard-sesiones" className={style.link}>
              <FaClipboardList className={style.icon} />
              Sesiones
            </Link>
          )}
          {showAllowedAdmin && (
            <Link to="/balance" className={style.link}>
              <FaBalanceScale className={style.icon} />
              Balance
            </Link>
          )}
        </div>
        {user && user.user && (
          <div className={style['user-details']}>
            {user.user.photo && <img src={user.user.photo} alt="User Photo" />}
            <p>{user.user.name} {user.user.last_name}</p>
          </div>
        )}
      </div>
      {isExpanded && (
        <div
          className={style.overlay}
          onClick={() => setIsExpanded(false)}
        />
      )}
    </div>
  );
};

export default Sidebar;
